import { Drawer, DrawerContent } from '@finn-auto/design-system/atoms/drawer';
import { PanelFeedback } from './PanelFeedback';
import { PanelSchedule } from './PanelSchedule';

/**
 * The candidate panel: one candidate's full picture, on the right, without leaving the board.
 *
 * A shell and a list of sections, deliberately. Each section owns its own fetch, its own empty
 * state and its own failure, so one slow or broken read never costs the others — a CV that cannot
 * be reached must not take the interview feedback down with it. Nothing is fetched until the panel
 * is opened; the board's first load must not pay for context nobody asked to see.
 *
 * Order is the order a recruiter asks: what did the last round say, then what has been scheduled.
 * CV and application answers come next, as sections in the same list.
 *
 * ⚠️ Everything in here is written by interviewers, candidates and colleagues. It is TEXT, never
 * markup, and it is a DISPLAY surface: nothing from it may be piped into a candidate-facing draft.
 */
const leverUrl = (opportunityId: string) => `https://hire.lever.co/candidates/${opportunityId}`;

export function CandidatePanel({
  boardEmail,
  opportunityId,
  candidateName,
  onClose,
}: {
  boardEmail: string;
  opportunityId: string;
  candidateName: string;
  onClose: () => void;
}) {
  return (
    <Drawer open direction="right" onOpenChange={(next: boolean) => !next && onClose()}>
      <DrawerContent
        childrenClassName="!gap-6 !p-6"
        renderHeader={() => (
          <div className="flex items-start justify-between gap-4 border-0 border-b border-solid border-zinc-200 p-6">
            <div className="min-w-0">
              <h2 className="truncate text-2xl font-bold text-zinc-900">{candidateName}</h2>
              <a
                href={leverUrl(opportunityId)}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-blue-600 hover:underline"
              >
                Open in Lever ↗
              </a>
            </div>
            {/* A plain <button>, not the DS `Button`, like the feedback drawer's own close control
                (feedback/FeedbackPanel.tsx): this is a 20px icon affordance in a header, and
                `Button`'s padding, height and centred label are a labelled-action shape. */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close candidate panel"
              className="shrink-0 rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            >
              ✕
            </button>
          </div>
        )}
      >
        <PanelFeedback boardEmail={boardEmail} opportunityId={opportunityId} />
        <PanelSchedule boardEmail={boardEmail} opportunityId={opportunityId} />
      </DrawerContent>
    </Drawer>
  );
}
